/**
 * #45 Destek Talepleri — destek EKİBİNİN kuyruğu.
 *
 * Saha temsilcisi/mobil kullanıcı destek sohbetini açtığında bir talep doğar; AI ilk
 * karşılamayı yapar ve çözemediğinde talep bu kuyruğa düşer (durum "Destek bekliyor").
 * Kullanıcının kendi sohbeti mobil uygulamadadır; burada yazışmayı destek personeli sürdürür.
 *
 * c360 liste altyapısı üzerine kuruludur (repo standardı).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, Toast, ExcelIcon, exportToExcel, toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination } from './ui';
import { useC360List, C360Table, ColumnChooser, ViewIcon, EditIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import './supportTickets.css';

const API_BASE = '/api/supporttickets';
const LIST_ROUTE = '/support-tickets';

// Sunucu tarafı SupportTicketStatus ile birebir (DataScope.Entities/Enums/SupportEnums.cs).
export const STATUS_LABELS = {
  0: 'AI ile görüşülüyor',
  1: 'Destek bekliyor',
  2: 'Destek ilgileniyor',
  3: 'Kullanıcı bekleniyor',
  4: 'Çözüldü',
  5: 'Kapandı',
};

// Kuyrukta olması gereken durumlar (rozet rengi için).
const STATUS_TONE = {
  0: 'muted',
  1: 'warn',
  2: 'info',
  3: 'info',
  4: 'ok',
  5: 'muted',
};

export const PRIORITY_LABELS = { 0: 'Normal', 1: 'Yüksek', 2: 'Kritik' };

export const CATEGORY_LABELS = {
  usage: 'Kullanım sorusu',
  error: 'Uygulama hatası',
  data: 'Veri düzeltme',
  permission: 'Yetki / erişim',
  other: 'Diğer',
};

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));
const PRIORITY_OPTIONS = Object.entries(PRIORITY_LABELS).map(([value, label]) => ({ value, label }));

const COLUMN_FILTER_META = {
  ticketNo: { type: 'number', odataField: 'TicketNo' },
  subject: { type: 'text', odataField: 'Subject' },
  userFullName: { type: 'text', odataField: 'UserFullName' },
  status: { type: 'select', odataField: 'Status', valueFormat: 'raw', options: STATUS_OPTIONS },
  priority: { type: 'select', odataField: 'Priority', valueFormat: 'raw', options: PRIORITY_OPTIONS },
  assignedUserName: { type: 'text', odataField: 'AssignedUserName' },
  screenTitle: { type: 'text', odataField: 'ScreenTitle' },
  lastMessageDate: { type: 'date', odataField: 'LastMessageDate' },
};

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('tr-TR') : '-');

export function SupportTicketsPage() {
  const navigate = useNavigate();
  usePageMeta('Destek Talepleri', 'Mobil ve web kullanıcılarından gelen destek talepleri.');

  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const searchFields = useMemo(() => ['Subject', 'UserFullName', 'AssignedUserName'], []);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields,
    // Kuyruk mantığı: en son hareket eden talep en üstte (bekleyeni gözden kaçırmamak için).
    defaultSortField: 'LastMessageDate',
    defaultSortDir: 'desc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    exportRows,
  } = list;

  const openDetail = (row, view = true) => {
    if (!row) return;
    navigate(`${LIST_ROUTE}/${row.id}${view ? '' : '?edit=1'}`, { state: { row, view } });
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('ticketNo', {
      header: 'Talep No', meta: { filterType: 'number' },
      cell: (info) => <span className="c360-erp-chip">#{info.getValue() ?? '-'}</span>,
    }),
    columnHelper.accessor('subject', {
      header: 'Konu', meta: { filterType: 'text' },
      cell: (info) => (
        <span>
          {info.row.original.unreadByAgent && <span className="sup-dot" title="Okunmamış kullanıcı mesajı" />}
          {info.getValue() || '-'}
        </span>
      ),
    }),
    columnHelper.accessor('userFullName', {
      header: 'Kullanıcı', meta: { filterType: 'text' }, cell: (i) => i.getValue() || '-',
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (i) => (
        <span className={`sup-badge sup-badge--${STATUS_TONE[i.getValue()] || 'muted'}`}>
          {STATUS_LABELS[i.getValue()] || '-'}
        </span>
      ),
    }),
    columnHelper.accessor('priority', {
      header: 'Öncelik', meta: { filterType: 'select' },
      cell: (i) => (i.getValue() ? (
        <span className={`sup-badge sup-badge--${i.getValue() === 2 ? 'danger' : 'warn'}`}>
          {PRIORITY_LABELS[i.getValue()]}
        </span>
      ) : PRIORITY_LABELS[0]),
    }),
    columnHelper.accessor('category', {
      header: 'Kategori', enableColumnFilter: false,
      cell: (i) => CATEGORY_LABELS[i.getValue()] || i.getValue() || '-',
    }),
    columnHelper.accessor('screenTitle', {
      header: 'Geldiği Ekran', meta: { filterType: 'text' }, cell: (i) => i.getValue() || '-',
    }),
    columnHelper.accessor('assignedUserName', {
      header: 'İlgilenen', meta: { filterType: 'text' },
      cell: (i) => i.getValue() || <span className="sup-muted">— atanmadı</span>,
    }),
    columnHelper.accessor('platform', {
      header: 'Platform', enableColumnFilter: false, cell: (i) => i.getValue() || '-',
    }),
    columnHelper.accessor('lastMessageDate', {
      header: 'Son Hareket', meta: { filterType: 'date' }, cell: (i) => fmtDateTime(i.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: '',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" style={s.iconBtn} title="İzle"
            onClick={(e) => { e.stopPropagation(); openDetail(row.original, true); }}>
            <ViewIcon />
          </button>
          <button type="button" style={s.iconBtn} title="Yazışmayı sürdür"
            onClick={(e) => { e.stopPropagation(); openDetail(row.original, false); }}>
            <EditIcon />
          </button>
        </div>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <ListPageShell
      title="Destek Talepleri"
      subtitle="AI'ın çözemediği talepler buraya düşer. Talebi açıp yazışmayı sürdürebilirsiniz."
      search={{
        value: filterInput,
        onChange: setFilterInput,
        onSubmit: applyFilterText,
        placeholder: 'Konu, kullanıcı veya ilgilenen ara…',
      }}
      actions={(
        <>
          <ColumnChooser table={table} />
          <button type="button" style={s.secondaryBtn}
            onClick={() => exportRows().then((data) => exportToExcel(data, 'destek-talepleri'))}>
            <ExcelIcon />Excel
          </button>
        </>
      )}
    >
      <C360Table
        table={table}
        loading={loading}
        rows={rows}
        selectedId={selected?.id}
        onRowClick={(row) => setSelected(row)}
        onRowDoubleClick={(row) => openDetail(row, true)}
        sortField={toGridSortField(sortField)}
        sortDir={sortDir}
        onSort={(field) => applySort(toODataSortField(field))}
        columnFilters={columnFilters}
        onColumnFilter={setColumnFilter}
        onClearColumnFilter={clearColumnFilter}
        emptyText={filterText ? 'Aramaya uygun talep yok.' : 'Henüz destek talebi yok.'}
      />
      <Pagination
        totalCount={totalCount}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
      <Toast toast={toast} />
    </ListPageShell>
  );
}

export default SupportTicketsPage;
